"use client";

import type { ReportData } from "./reportData";

interface AvatarStatementSectionProps {
  report: ReportData;
  name: string;
}

export default function AvatarStatementSection({ report, name }: AvatarStatementSectionProps) {
  const { avatarStatement, finalMessage } = report;

  return (
    <section className="fade-in-up space-y-5">
      {/* Avatar Statement */}
      <div
        className="rounded-2xl p-8 md:p-10 text-center relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(124,62,97,0.3) 0%, rgba(0,18,88,0.5) 50%, rgba(54,23,64,0.6) 100%)",
          border: "1px solid rgba(219,184,77,0.4)",
          boxShadow: "0 0 40px rgba(219,184,77,0.08), 0 16px 40px rgba(0,0,0,0.3)",
        }}
      >
        {/* Decorative elements */}
        <div
          className="absolute top-0 left-0 w-full h-px"
          style={{ background: "linear-gradient(90deg, transparent, rgba(219,184,77,0.5), transparent)" }}
        />
        <div
          className="absolute bottom-0 left-0 w-full h-px"
          style={{ background: "linear-gradient(90deg, transparent, rgba(219,184,77,0.3), transparent)" }}
        />
        <div
          className="absolute top-6 left-6 text-5xl opacity-10 pointer-events-none select-none"
          aria-hidden="true"
        >
          "
        </div>
        <div
          className="absolute bottom-6 right-6 text-5xl opacity-10 pointer-events-none select-none"
          aria-hidden="true"
        >
          "
        </div>

        {/* Label */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div
            className="h-px w-12"
            style={{ background: "linear-gradient(90deg, transparent, rgba(219,184,77,0.5))" }}
          />
          <span
            className="text-xs font-600 tracking-widest uppercase"
            style={{ color: "rgba(219,184,77,0.7)", letterSpacing: "0.14em", fontWeight: 600 }}
          >
            Your Avatar Statement
          </span>
          <div
            className="h-px w-12"
            style={{ background: "linear-gradient(90deg, rgba(219,184,77,0.5), transparent)" }}
          />
        </div>

        {/* Name */}
        <p
          className="text-sm font-500 mb-4"
          style={{ color: "rgba(219,184,77,0.65)", fontWeight: 500 }}
        >
          {name}
        </p>

        {/* Statement */}
        <blockquote
          className="text-lg md:text-xl lg:text-2xl font-display text-white/90 leading-relaxed italic max-w-2xl mx-auto"
          style={{ fontWeight: 500 }}
        >
          {avatarStatement}
        </blockquote>
      </div>

      {/* Final Message */}
      <div
        className="rounded-2xl p-7 md:p-8"
        style={{
          background: "linear-gradient(135deg, rgba(54,23,64,0.7) 0%, rgba(0,18,88,0.4) 100%)",
          border: "1px solid rgba(219,184,77,0.2)",
        }}
      >
        <div className="flex items-center gap-2 mb-4">
          <div
            className="w-1 h-5 rounded-full"
            style={{ background: "linear-gradient(180deg, #dbb84d, rgba(219,184,77,0.3))" }}
          />
          <h3
            className="text-base font-display text-white/90"
            style={{ fontWeight: 600 }}
          >
            A Final Word for You
          </h3>
        </div>
        <p className="text-sm md:text-base text-white/70 leading-relaxed" style={{ fontWeight: 400 }}>
          {finalMessage}
        </p>
      </div>
    </section>
  );
}